/**
 * Load a process-test's answered trace into the scrubber (P7).
 *
 * Clicking a test in the TestsPanel replays its `when` pins as walker branch
 * selections. The pin→arm resolution is the engine's canonical one
 * (`resolvePinnedBranches`, the same map `enumerateTraces` uses) — a positive
 * pin selects its arm, a negative (`no`/`never`) pin falls to the `else`/other
 * arm — so the UI never re-derives (and diverges from) the trace semantics.
 * `select`/`parallel`/`await` pins carry no branch selection and are absent from
 * the map; the scrubber is parked at the trace start.
 */

import { resolvePinnedBranches, walkerContextFor, type ProcessModel, type ResolvedModel, type TestDecl } from "@archlang/engine";

/** The playback surface this helper drives — the minimal subset it needs, so
 *  it's trivially mockable in tests. */
export interface PinApplyTarget {
  /** Replace the active process's branch selections wholesale (clears prior). */
  readonly replaceBranchSelection: (selections: ReadonlyMap<string, string>) => void;
  readonly setActiveStepIndex: (index: number | null) => void;
}

/**
 * Load a test's answered trace into the scrubber: resolve its pins to the
 * canonical if/try branch selections and REPLACE the walker's selections with
 * them (so a replay never inherits a previously-clicked test's arms), then park
 * the scrubber at the trace start. Returns the number of branch selections.
 */
export function applyTestPins(
  test: TestDecl,
  process: ProcessModel,
  model: ResolvedModel,
  playback: PinApplyTarget,
): number {
  const ctx = walkerContextFor(model, process);
  const selections = resolvePinnedBranches(process, test.pins, ctx);
  playback.replaceBranchSelection(selections);
  playback.setActiveStepIndex(0);
  return selections.size;
}
